import React, { useCallback, useState, useContext, useMemo } from 'react';
import { isArray, result, first, noop, difference } from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Stack, Callout, DefaultButton } from '@bingads-webui-react/fluent-controls';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useEventLog } from '@bingads-webui-campaign-react/copilot-common';
import { withFluentSwitcher } from '@bingads-webui-react/fluent-version-switcher';
import { ValidatedFinalUrl as ValidatedFinalUrlV1 } from '@bingads-webui-campaign-react/validated-url-input';
import { ValidatedFinalUrl as ValidatedFinalUrlV2 } from '@bingads-webui-campaign-react/validated-url-input-fluent-v2';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { getUrlPrefixAndDestination } from '@bingads-webui-campaign-react/url-input-fluent';
import { getFinalUrlRecommendationsStyles } from './styles';

const FinalURlKey = 'FinalUrls';

const ValidatedFinalUrl = withFluentSwitcher(ValidatedFinalUrlV1, ValidatedFinalUrlV2);

export const ValidatedFinalUrlWithRecommendations= (props) => {
  const {
    updateValidation,
    validationProps,
    additionalProperties,
    i18n,
    enableUrlRecommendation,
    enableCopilotRecommendations,
    enableSpecialCharactersValidation,
  } = props;
  const classes = useDefaultStyles(getFinalUrlRecommendationsStyles);
  const createFinalUrlsViewModel = urls => ({ [FinalURlKey]: urls });
  const [showFinalUrlRecommendations, setShowFinalUrlRecommendations] = useState(false);
  const [recommendationContext] = useContext(RecommendationContext);

  const { postAssetEventLog = noop, copilotTracking } = recommendationContext;
  const { logFinalUrlRecommendations } = useEventLog(postAssetEventLog);

  const logCopilotSuggestions = useCallback((suggestions) => {
    if (copilotTracking) {
      const suggestionsToLog = difference(suggestions, copilotTracking.trackingData.viewedSuggestions);
      if (suggestionsToLog.length > 0) {
        logFinalUrlRecommendations(suggestionsToLog);
      }
      copilotTracking.trackViewedSuggestionIds(suggestionsToLog);
    }
  }, [copilotTracking, logFinalUrlRecommendations]);

  const currentFinalUrlDestinationValue = useMemo(() => {
    const { data } = validationProps;
    const finalUrls = result(data, FinalURlKey, undefined);
    const [, dest] = getUrlPrefixAndDestination(first(finalUrls));
    return dest;
  }, [validationProps]);

  const onClickOrChangeHandler = useCallback(() => {
    setShowFinalUrlRecommendations(isArray(recommendationContext.finalUrls) && recommendationContext.finalUrls.length > 0);
  }, [recommendationContext.finalUrls]);

  const onDismissFinalUrlRecommendations = useCallback(() => {
    setShowFinalUrlRecommendations(false);
  }, []);

  const applyRecommendation = useCallback((finalUrlRecommendation) => {
    updateValidation([], createFinalUrlsViewModel([finalUrlRecommendation]));
    onDismissFinalUrlRecommendations();
  }, [onDismissFinalUrlRecommendations, updateValidation]);

  const renderRecommendations = useCallback(() => {
    const filteredRecommendations = recommendationContext.finalUrls.filter((recommendation) => {
      const [, recommendationDestination] = getUrlPrefixAndDestination(recommendation);

      return recommendationDestination.toLowerCase().startsWith(currentFinalUrlDestinationValue.toLowerCase());
    });

    if (filteredRecommendations.length === 0) {
      return null;
    }
    if (enableCopilotRecommendations) {
      logCopilotSuggestions(filteredRecommendations);
    }

    return (
      <div className={classnames('text-final-url-recommendation-content', classes.TextFinalUrlRecommendationsContent)}>
        <Stack>
          <span className="text-recommendation-title">{i18n.getString(_TL_('Recommended Final URL'))}</span>
          {filteredRecommendations.map(finalUrlRecommendation => <DefaultButton onClick={() => applyRecommendation(finalUrlRecommendation)}>{finalUrlRecommendation}</DefaultButton>)}
        </Stack>
      </div>);
  }, [recommendationContext.finalUrls, enableCopilotRecommendations, classes.TextFinalUrlRecommendationsContent, i18n, currentFinalUrlDestinationValue, logCopilotSuggestions, applyRecommendation]);

  return (
    <React.Fragment>
      <ValidatedFinalUrl
        {...props}
        additionalProperties={{
          onClick: onClickOrChangeHandler,
          ...additionalProperties,
        }}
        enableSpecialCharactersValidation={enableSpecialCharactersValidation}
      />
      {enableUrlRecommendation && showFinalUrlRecommendations &&
        <Callout
          target=".final-url .url-field input"
          isBeakVisible={false}
          gapSpace={0}
          role="dialog"
          onDismiss={onDismissFinalUrlRecommendations}
        >
          {renderRecommendations()}
        </Callout>
      }
    </React.Fragment>
  );
};

ValidatedFinalUrlWithRecommendations.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      FinalUrls: PropTypes.arrayOf(PropTypes.string),
    }),
    updateOnMount: PropTypes.bool,
  }).isRequired,
  enableCopilotRecommendations: PropTypes.bool,
  enableUrlRecommendation: PropTypes.bool,
  enableSpecialCharactersValidation: PropTypes.bool,
  additionalProperties: PropTypes.shape({}),
};

ValidatedFinalUrlWithRecommendations.defaultProps = {
  additionalProperties: {},
  enableCopilotRecommendations: true,
  enableUrlRecommendation: true,
  enableSpecialCharactersValidation: false,
};
